/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import CopyToClipboard from '../../copy-to-clipboard';

const styles = cssMap({
	button: {
		textAlign: 'left',
		width: '100%',
		paddingBlock: token('space.075'),
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
		transition: token('motion.button.hovered'),
	},
	buttonMotion: {
		textAlign: 'left',
		width: '100%',
		paddingBlock: token('space.075'),
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text'),
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	copyIconContainer: {
		flexGrow: 1,
		textAlign: 'right',
	},
	objectName: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.xsmall'),
		font: token('font.code'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
	},
	objectNameHover: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

type CodeObjectCopyButtonProps = {
	name: string;
	objectElement: React.ReactNode;
	onClick: () => void;
};

const CodeObjectCopyButton = ({ name, objectElement, onClick }: CodeObjectCopyButtonProps) => {
	const [isMouseOver, setIsMouseOver] = useState(false);

	const mouseEnterHandler = () => {
		setIsMouseOver(true);
	};

	const mouseLeaveHandler = () => {
		setIsMouseOver(false);
	};

	return (
		<Pressable
			xcss={styles.buttonMotion}
			onMouseEnter={mouseEnterHandler}
			onMouseLeave={mouseLeaveHandler}
			onClick={() => {
				onClick();
			}}
		>
			<Inline space="space.100" alignBlock="center">
				{objectElement}
				<Box xcss={cx(styles.objectName, isMouseOver && styles.objectNameHover)}>{name}</Box>
				<Box xcss={styles.copyIconContainer}>
					<CopyIcon label="" color="currentColor" />
				</Box>
			</Inline>
		</Pressable>
	);
};

type CodeObjectCopyToClipboardProps = {
	value: string;
	name: string;
	objectElement: React.ReactNode;
	prompt: string;
};

/**
 * __Code object copy to clipboard__
 *
 * A component that displays an object, size name and a statement which can be copied to the clipboard when clicked.
 */
const CodeObjectCopyToClipboard: ({
	value,
	prompt,
	name,
	objectElement,
}: CodeObjectCopyToClipboardProps) => JSX.Element = ({
	value,
	prompt,
	name,
	objectElement,
}: CodeObjectCopyToClipboardProps) => {
	return (
		<CopyToClipboard
			value={value}
			messages={{
				prompt: prompt,
				success: `Copied "${value}"`,
				fail: 'Copy failed',
			}}
			resetMs={2000}
		>
			{({ copy }) => (
				<CodeObjectCopyButton onClick={copy} name={name} objectElement={objectElement} />
			)}
		</CopyToClipboard>
	);
};

export default CodeObjectCopyToClipboard;
